import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import api from "@/services/api.js";
import logo from "@/assets/logo.png";
import toast from "react-hot-toast";
import { capture } from "@/instrument";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoading } from "@/context/LoadingContext.jsx";
import { HiChevronDown, HiOutlineTrash, HiOutlinePlus } from "react-icons/hi";
import DateInput from "@/components/DateInput";
import TimeInput from "@/components/TimeInput";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { compareByOrder } from "../utils";

const getCurrentSegment = (answer) => {
  if (!answer) return "";
  const parts = answer.split(";");
  return parts[parts.length - 1].trim();
};

const CheckInQuestions = () => {
  const navigate = useNavigate();
  const isMobileDevice = useMemo(() => /Mobi|Android/i.test(navigator.userAgent), []);
  const [searchParams] = useSearchParams();
  const validationToken = searchParams.get("token");
  const id = searchParams.get("id");
  const laboratoryToken = searchParams.get("laboratoryToken");
  const { startLoading, stopLoading } = useLoading();
  const timeoutRef = useRef(null);

  const questionRecordDocumentPickers = useRef({});
  const [questionRecords, setQuestionRecords] = useState([]);
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [questionRecordDocuments, setQuestionRecordDocuments] = useState({});
  const [dragOver, setDragOver] = useState({});
  const [openAutocompleteQuestionRecordId, setOpenAutocompleteQuestionRecordId] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [animateParentRef] = useAutoAnimate();

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getData = async () => {
    try {
      startLoading();

      const res = await api.post(`/admission/check-in?id=${id}&token=${validationToken}`, {});
      if (!res.ok) throw res;

      const unansweredQuestionRecords = res.data.questionRecords?.filter((qr) => !qr.answer) || [];

      if (unansweredQuestionRecords.length === 0) {
        navigate(`/guidelines?id=${id}&token=${validationToken}&laboratoryToken=${laboratoryToken}`);
        return;
      }

      setQuestionRecords(unansweredQuestionRecords);
      setFullName(res.data.fullName || res.data.patient?.firstName + " " + res.data.patient?.lastName);

      let existingDocuments = {};
      for (const questionRecord of unansweredQuestionRecords) {
        if (questionRecord.answerType === "DOCUMENT" && questionRecord.answer) {
          existingDocuments[questionRecord._id] = {
            preview: { name: questionRecord.answer.split("/").pop() || "Document" },
            file: null,
          };
        }
      }
      setQuestionRecordDocuments(existingDocuments);
    } catch (e) {
      if (e?.code === "UNAUTHORIZED") return navigate("/");
      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      stopLoading();
    }
  };

  const handleFile = async (file, questionRecordId) => {
    if (!file || !questionRecordId) return;

    const acceptedTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!acceptedTypes.includes(file.type)) {
      toast.error("Format de fichier non supporté. Veuillez utiliser une image (PNG, JPG) ou un PDF.");
      return;
    }

    const fileType = file.type === "application/pdf" ? "pdf" : "image";
    const fileUrl = URL.createObjectURL(file);

    setQuestionRecordDocuments((prev) => ({
      ...prev,
      [questionRecordId]: { preview: { type: fileType, url: fileUrl, name: file.name }, file },
    }));

    const questionRecord = questionRecords.find((qr) => qr._id === questionRecordId);
    await updateQuestionRecords([questionRecord], [{ file, name: `document-${questionRecord.questionId}` }]);
  };

  const handleDragOver = (e, questionRecordId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => ({ ...prev, [questionRecordId]: true }));
  };

  const handleDragLeave = (e, questionRecordId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => ({ ...prev, [questionRecordId]: false }));
  };

  const handleDrop = (e, questionRecordId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => ({ ...prev, [questionRecordId]: false }));

    const file = e.dataTransfer.files[0];
    if (file) handleFile(file, questionRecordId);
  };

  const updateQuestionRecords = async (questionRecordsToUpdate, filesObj = []) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("questionRecords", JSON.stringify(questionRecordsToUpdate));
      formData.append("isFinalSubmission", "false");
      formData.append("checkIn", "true");

      for (const { name, file } of filesObj) {
        formData.append(name, file);
      }

      const res = await api.putFormData(`/admission/questionRecord?id=${id}&token=${validationToken}`, formData);

      if (!res.ok) return setLoading(false);

      const responseQuestionRecords = res.data.questionRecords;

      setQuestionRecords((prev) => {
        const updatedList = prev.map((questionRecord) => {
          const responseQuestionRecord = responseQuestionRecords.find((qr) => qr._id === questionRecord._id);
          if (responseQuestionRecord && questionRecord.answerType === "DOCUMENT") return responseQuestionRecord;
          return questionRecord;
        });

        const newQuestionRecords = responseQuestionRecords.filter((qr) => !prev.find((prevQr) => prevQr._id === qr._id));

        return [...updatedList, ...newQuestionRecords];
      });
    } catch (e) {
      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    }
    setLoading(false);
  };

  const handleAnswerChange = useCallback(
    async (questionRecordId, value) => {
      const updatedQuestionRecords = JSON.parse(JSON.stringify(questionRecords));
      const questionRecord = updatedQuestionRecords.find((qr) => qr._id === questionRecordId);
      if (!questionRecord) return;

      questionRecord.answer = value;
      setQuestionRecords(updatedQuestionRecords);

      setLoading(true);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => updateQuestionRecords(updatedQuestionRecords), 250);
    },
    [questionRecords, updateQuestionRecords],
  );

  const submitAnswers = async () => {
    setSubmissionAttempted(true);

    const unansweredQuestionRecords = questionRecords.filter((questionRecord) => {
      if (questionRecord.patientIsRequired === false) return false;
      if (questionRecord.answerType === "DOCUMENT") {
        return !questionRecordDocuments[questionRecord._id];
      }
      return !questionRecord.answer;
    });
    if (unansweredQuestionRecords.length > 0) {
      const questionRecordElement = document.querySelector(`[data-question-record-id="${unansweredQuestionRecords[0]._id}"]`);
      if (questionRecordElement) {
        questionRecordElement.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => questionRecordElement.focus(), 300);
      }
      return toast.error(`${unansweredQuestionRecords.length} question${unansweredQuestionRecords.length > 1 ? "s" : ""} sans réponse`);
    }

    try {
      startLoading();

      const formData = new FormData();
      formData.append("questionRecords", JSON.stringify(questionRecords));
      formData.append("checkIn", "true");
      for (const questionRecord of questionRecords) {
        const file = questionRecordDocuments[questionRecord._id]?.file;
        if (file) formData.append(`document-${questionRecord.questionId}`, file);
      }
      const res = await api.putFormData(`/admission/questionRecord?id=${id}&token=${validationToken}`, formData);
      if (!res.ok) throw res;
      navigate(`/guidelines?id=${id}&token=${validationToken}&laboratoryToken=${laboratoryToken}`);
    } catch (e) {
      capture(e);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      stopLoading();
    }
  };

  const isQuestionRecordUnanswered = (questionRecord) => {
    if (!submissionAttempted) return false;
    if (questionRecord.patientIsRequired === false) return false;
    if (questionRecord.answerType === "DOCUMENT") return !questionRecordDocuments[questionRecord._id]?.preview?.name;
    return !questionRecord.answer;
  };

  return (
    <div className="container mx-auto min-w-full font-sans text-center">
      <div className="p-5 pb-44">
        <div className="mt-5">
          <img src={logo} alt="Logo" className="mx-auto h-12" />
        </div>
        <div className="container mx-auto font-sans">
          <div className="mt-4 w-full text-left p-2 rounded-lg">
            {questionRecords.length > 0 && (
              <>
                <p className="font-bold text-lg text-primary">Questions</p>
                <div className="text-lg mt-2 mb-4">
                  Bonjour <span className="text-lg font-bold">{fullName}</span>, nous avons besoin de quelques informations supplémentaires :
                </div>
                <div ref={animateParentRef}>
                  {[...questionRecords].sort(compareByOrder).map((questionRecord) => {
                    const choicesWithLabels = (questionRecord.options || []).map((o) => ({ optionId: o.optionId, value: o.label }));
                    return (
                    <div key={questionRecord._id} className="mt-4 mb-4 text-left">
                      <p className="font-semibold">
                        {questionRecord.question || ""}
                        {questionRecord.patientIsRequired !== false && <span className="text-red-500 ml-1">*</span>}
                      </p>

                      {questionRecord.answerType === "MULTIPLE_CHOICE" ? (
                        <div className="relative">
                          <select
                            data-question-record-id={questionRecord._id}
                            value={questionRecord.answer || ""}
                            onChange={(e) => handleAnswerChange(questionRecord._id, e.target.value)}
                            className={`block w-full mt-2 py-2 px-3 bg-white border ${
                              isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                            } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                          >
                            <option value="" disabled>
                              Sélectionnez une réponse
                            </option>
                            {choicesWithLabels.map((choice) => (
                              <option key={choice.optionId} value={choice.optionId}>
                                {choice.value}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-primary">
                            <HiChevronDown className="w-5 h-5" />
                          </div>
                          {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                        </div>
                      ) : questionRecord.answerType === "TEXT" ? (
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              data-question-record-id={questionRecord._id}
                              value={questionRecord.answer || ""}
                              onChange={(e) => {
                                handleAnswerChange(questionRecord._id, e.target.value);
                                if (choicesWithLabels.length > 0) {
                                  setOpenAutocompleteQuestionRecordId(questionRecord._id);
                                  setSelectedSuggestionIndex(0);
                                }
                              }}
                              onFocus={() => {
                                if (choicesWithLabels.length > 0) {
                                  setOpenAutocompleteQuestionRecordId(questionRecord._id);
                                  setSelectedSuggestionIndex(0);
                                }
                              }}
                              onBlur={() => {
                                setTimeout(() => {
                                  setOpenAutocompleteQuestionRecordId(null);
                                  setSelectedSuggestionIndex(0);
                                }, 200);
                              }}
                              onKeyDown={(e) => {
                                if (choicesWithLabels.length > 0 && openAutocompleteQuestionRecordId === questionRecord._id) {
                                  const currentSegment = getCurrentSegment(questionRecord.answer);
                                  const filtered = choicesWithLabels.filter(
                                    (s) => !currentSegment || s.value.toLowerCase().includes(currentSegment.toLowerCase()),
                                  );

                                  if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    setSelectedSuggestionIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
                                  } else if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
                                  } else if (e.key === "Enter" && filtered.length > 0) {
                                    e.preventDefault();
                                    const selectedOption = filtered[selectedSuggestionIndex];
                                    if (selectedOption) {
                                      const existingParts = (questionRecord.answer || "").split(";").slice(0, -1).map((p) => p.trim()).filter(Boolean);
                                      const newAnswer = [...existingParts, selectedOption.value].join("; ") + "; ";
                                      handleAnswerChange(questionRecord._id, newAnswer);
                                      setSelectedSuggestionIndex(0);
                                    }
                                  } else if (e.key === "Escape") {
                                    setOpenAutocompleteQuestionRecordId(null);
                                    setSelectedSuggestionIndex(0);
                                  }
                                }
                              }}
                              className={`block w-full mt-2 py-2 px-3 bg-white border ${
                                isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                              } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                              placeholder="Votre réponse"
                            />
                            {choicesWithLabels.length > 0 && openAutocompleteQuestionRecordId === questionRecord._id && (() => {
                              const currentSegment = getCurrentSegment(questionRecord.answer);
                              const filtered = choicesWithLabels.filter(
                                (s) => !currentSegment || s.value.toLowerCase().includes(currentSegment.toLowerCase()),
                              );
                              if (filtered.length === 0) return null;
                              return (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                                  {filtered.map((option, sugIndex) => (
                                    <button
                                      key={sugIndex}
                                      type="button"
                                      className={`w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                                        sugIndex === selectedSuggestionIndex ? "bg-gray-100" : ""
                                      }`}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        const existingParts = (questionRecord.answer || "").split(";").slice(0, -1).map((p) => p.trim()).filter(Boolean);
                                        const newAnswer = [...existingParts, option.value].join("; ") + "; ";
                                        handleAnswerChange(questionRecord._id, newAnswer);
                                        setSelectedSuggestionIndex(0);
                                        setOpenAutocompleteQuestionRecordId(null);
                                      }}
                                      onMouseEnter={() => setSelectedSuggestionIndex(sugIndex)}
                                    >
                                      {option.value}
                                    </button>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                          {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                        </>
                      ) : questionRecord.answerType === "DATE" ? (
                        <>
                          <DateInput
                            data-question-record-id={questionRecord._id}
                            value={questionRecord.answer || ""}
                            onChange={(date) => handleAnswerChange(questionRecord._id, date)}
                            className={`block w-full mt-2 py-2 px-3 bg-white border ${
                              isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                            } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                          />
                          {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                        </>
                      ) : questionRecord.answerType === "HOUR" ? (
                        <>
                          <TimeInput
                            data-question-record-id={questionRecord._id}
                            value={questionRecord.answer || ""}
                            onChange={(time) => handleAnswerChange(questionRecord._id, time)}
                            className={`block w-full mt-2 py-2 px-3 bg-white border ${
                              isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"
                            } rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary h-10 min-w-full box-border`}
                          />
                          {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                        </>
                      ) : questionRecord.answerType === "DOCUMENT" ? (
                        <>
                          {questionRecordDocuments[questionRecord._id] ? (
                            <div className="relative group bg-gray-50 rounded-lg overflow-hidden shadow-sm max-h-[200px] mt-2">
                              <div className="aspect-w-4 aspect-h-3 h-[150px]">
                                {questionRecordDocuments[questionRecord._id]?.preview?.type === "pdf" ? (
                                  <embed src={questionRecordDocuments[questionRecord._id].preview.url} type="application/pdf" className="w-full h-full pointer-events-none" />
                                ) : questionRecordDocuments[questionRecord._id]?.preview?.url ? (
                                  <img
                                    src={questionRecordDocuments[questionRecord._id].preview.url}
                                    alt={`Document ${questionRecord.question || ""}`}
                                    className="object-contain w-full h-full"
                                  />
                                ) : (
                                  <div className="flex items-center justify-center h-full text-gray-500">
                                    <p className="text-sm">Document téléchargé</p>
                                  </div>
                                )}
                              </div>
                              <div className="p-3 flex items-center justify-between bg-white">
                                <span className="text-sm font-medium text-gray-700 truncate">{questionRecordDocuments[questionRecord._id]?.preview?.name || "Document"}</span>
                                <button
                                  onClick={() => {
                                    setQuestionRecordDocuments((prev) => {
                                      const newState = { ...prev };
                                      delete newState[questionRecord._id];
                                      return newState;
                                    });

                                    updateQuestionRecords([{ ...questionRecord, answer: "" }]);
                                  }}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                >
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <input
                                data-question-record-id={questionRecord._id}
                                ref={(el) => {
                                  if (el) questionRecordDocumentPickers.current[questionRecord._id] = el;
                                }}
                                accept="image/png,image/jpeg,image/jpg,application/pdf"
                                type="file"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleFile(file, questionRecord._id);
                                }}
                              />
                              <div
                                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer mt-2 ${
                                  dragOver[questionRecord._id] ? "border-primary bg-primary/5" : "border-gray-300 hover:border-primary"
                                } ${isQuestionRecordUnanswered(questionRecord) ? "border-red-500 ring-1 ring-red-500" : ""} text-gray-400 hover:text-primary`}
                                onClick={() => questionRecordDocumentPickers.current[questionRecord._id]?.click()}
                                onDragOver={(e) => handleDragOver(e, questionRecord._id)}
                                onDragLeave={(e) => handleDragLeave(e, questionRecord._id)}
                                onDrop={(e) => handleDrop(e, questionRecord._id)}
                              >
                                <HiOutlinePlus className="w-8 h-8 mb-4" />
                                <p className="text-lg font-medium text-gray-700 text-center">
                                  {isMobileDevice ? "Prendre une photo ou ajouter un document" : "Cliquez pour ajouter un document"}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">ou glissez-déposez votre fichier ici</p>
                              </div>
                            </>
                          )}
                          {isQuestionRecordUnanswered(questionRecord) && <p className="text-red-500 text-xs mt-1">Cette question requiert une réponse</p>}
                        </>
                      ) : null}
                    </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-t border-gray-200 shadow-lg px-5 py-4">
        <div className="container mx-auto max-w-2xl">
          <p className="text-sm text-gray-500 mb-2 text-center">
            <span className="text-red-500 mr-1">*</span>
            Veuillez remplir tous les champs obligatoires pour continuer
          </p>
          <div className="flex gap-4 items-center justify-center w-full">
            <button
              disabled={loading}
              className="px-4 py-2 rounded-md border-primary border text-primary hover:text-primary-600 hover:border-primary-600"
              onClick={() => navigate(-1)}
            >
              Retour
            </button>
            <button
              className="flex-1 px-4 py-2 rounded-md bg-primary text-white hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              onClick={submitAnswers}
              disabled={loading}
            >
              {loading ? "Chargement..." : "Valider"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckInQuestions;
